import type { OrderItem } from './types';
import { fmtDateTime, fmtPrice } from './utils';

interface Props {
  orders: OrderItem[];
}

/** 订单状态 → 中文标签与配色 */
function statusBadge(status: string): { text: string; cls: string } {
  switch (status) {
    case 'paid':
      return { text: '已支付', cls: 'bg-green-100 text-green-700' };
    case 'pending':
      return { text: '待支付', cls: 'bg-amber-100 text-amber-700' };
    case 'closed':
      // 超时关单与转账被拒都记 closed（schema 注释：pending/paid/expired/refunded/closed），
      // 之前这张表没这一档，界面直接露出英文原值。
      return { text: '已关闭', cls: 'bg-gray-100 text-gray-500' };
    case 'expired':
      return { text: '已过期', cls: 'bg-gray-100 text-gray-500' };
    case 'cancelled':
    case 'canceled':
      return { text: '已取消', cls: 'bg-gray-100 text-gray-500' };
    case 'failed':
      return { text: '支付失败', cls: 'bg-rose-100 text-rose-600' };
    case 'refunded':
      return { text: '已退款', cls: 'bg-gray-100 text-gray-500' };
    default:
      return { text: status, cls: 'bg-gray-100 text-gray-500' };
  }
}

/** 升级记录列表：展示最近若干条升级/续费订单及支付状态 */
export function UpgradeHistory({ orders }: Props) {
  if (orders.length === 0) return null;
  const recent = orders.slice(0, 5);

  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-800 mb-3">升级记录</h3>
      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-3 py-2 text-gray-500 font-medium">时间</th>
              <th className="text-center px-3 py-2 text-gray-500 font-medium">套餐</th>
              <th className="text-center px-3 py-2 text-gray-500 font-medium">付款方式</th>
              <th className="text-right px-3 py-2 text-gray-500 font-medium">金额</th>
              <th className="text-center px-3 py-2 text-gray-500 font-medium">状态</th>
            </tr>
          </thead>
          <tbody>
            {recent.map((o) => {
              const badge = statusBadge(o.status);
              return (
                <tr key={o.id} className="border-b border-gray-100 last:border-0">
                  <td className="px-3 py-2 text-gray-600">{fmtDateTime(o.createdAt)}</td>
                  <td className="text-center px-3 py-2 text-gray-700">{o.durationMonths} 个月</td>
                  <td className="text-center px-3 py-2 text-gray-700">
                    {/* 名字一律由后端下发（这一列历史上存过两种值，前端无从判断）。
                        缺名时分两种真实情况说：压根没选过渠道 vs 选的渠道已被删掉。 */}
                    {o.paymentChannelName ?? (o.paymentChannel ? '渠道已下线' : '未选择')}
                  </td>
                  <td className="text-right px-3 py-2 text-gray-700">
                    {o.discountAmount > 0 ? (
                      <span>
                        <span className="line-through text-gray-400 mr-1">¥{fmtPrice(o.originalAmount)}</span>¥{fmtPrice(o.finalAmount)}
                      </span>
                    ) : (
                      `¥${fmtPrice(o.finalAmount)}`
                    )}
                  </td>
                  <td className="text-center px-3 py-2">
                    <span className={`px-2 py-0.5 rounded-full font-medium ${badge.cls}`}>{badge.text}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
